import React from 'react';
import {
  Grid,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Autocomplete,
  TextField,
  Box,
} from '@mui/material';
import PropTypes from 'prop-types';
import ReminderTimeList from './ReminderTimeList';

const ReminderScheduleConfig = ({
  schedule,
  onScheduleChange,
  timezones,
  templates,
  communicationEndpoints,
  channel,
}) => {

  const handleScheduleChange = (field, value) => {
    const updatedSchedule = {
      ...schedule,
      [field]: value,
    };

    // If switching to relative schedule, initialize reminder times with relative values
    if (field === 'use_relative_schedule' && value === true) {
      updatedSchedule.reminder_times = schedule.reminder_times.map(time => ({
        ...time,
        days_before_relative: time.days_before_relative || 0,
        hours_before: time.hours_before || 0,
        minutes_before: time.minutes_before || 0,
        days_before: null,
        time: null
      }));
    }
    // If switching to absolute schedule, initialize reminder times with absolute values
    else if (field === 'use_relative_schedule' && value === false) {
      updatedSchedule.reminder_times = schedule.reminder_times.map(time => ({
        ...time,
        days_before: time.days_before || 0,
        time: time.time || '',
        days_before_relative: null,
        hours_before: null,
        minutes_before: null
      }));
    }

    onScheduleChange(updatedSchedule);
  };

  const handleReminderTimesChange = (reminderTimes) => {
    onScheduleChange({
      ...schedule,
      reminder_times: reminderTimes,
    });
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Schedule Settings
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Autocomplete
            options={timezones}
            getOptionLabel={(option) => option.label}
            value={timezones.find(tz => tz.value === schedule.timezone) || null}
            onChange={(_, newValue) => {
              handleScheduleChange('timezone', newValue?.value || 'UTC');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Timezone"
                required
                fullWidth
                error={!schedule.timezone}
                helperText={!schedule.timezone ? 'Timezone is required' : ''}
              />
            )}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            groupBy={(option) => option.value.split('/')[0]}
            renderGroup={(params) => (
              <li key={params.group}>
                <Box sx={{ 
                  p: 1, 
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {params.group}
                  </Typography>
                </Box>
                <ul style={{ padding: 0, margin: 0 }}>
                  {params.children}
                </ul>
              </li>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={schedule.business_hours_only}
                onChange={(e) => handleScheduleChange('business_hours_only', e.target.checked)}
              />
            }
            label="Business Hours Only"
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={schedule.use_relative_schedule}
                onChange={(e) => handleScheduleChange('use_relative_schedule', e.target.checked)}
              />
            }
            label="Use Relative Schedule"
          />
        </Grid>
        <Grid item xs={12}>
          <ReminderTimeList
            reminderTimes={schedule.reminder_times || []}
            onReminderTimesChange={handleReminderTimesChange}
            templates={templates}
            communicationEndpoints={communicationEndpoints}
            channel={channel}
            timezones={timezones}
            useRelativeSchedule={schedule.use_relative_schedule}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

ReminderScheduleConfig.propTypes = {
  schedule: PropTypes.shape({
    timezone: PropTypes.string.isRequired,
    business_hours_only: PropTypes.bool.isRequired,
    use_relative_schedule: PropTypes.bool.isRequired,
    reminder_times: PropTypes.arrayOf(
      PropTypes.shape({
        days_before: PropTypes.number,
        time: PropTypes.string,
        days_before_relative: PropTypes.number,
        hours_before: PropTypes.number,
        minutes_before: PropTypes.number,
        messages: PropTypes.arrayOf(
          PropTypes.shape({
            template: PropTypes.string,
            content: PropTypes.string,
            email_config: PropTypes.object,
            sms_config: PropTypes.object,
            voice_config: PropTypes.object,
            chat_config: PropTypes.object,
          })
        ),
      })
    ).isRequired,
  }).isRequired,
  onScheduleChange: PropTypes.func.isRequired,
  timezones: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  channel: PropTypes.oneOf(['email', 'sms', 'voice', 'chat']).isRequired,
};

export default ReminderScheduleConfig; 